import React, { useState, useEffect } from "react";
import Flexbox from "flexbox-react";
import { Dialog, InputGroup, FormGroup, HTMLSelect, Icon } from "@blueprintjs/core";
import ScrollableDropdown from "../shared/ScrollableDropdown";
import { FilledButton, OutlinedButton } from "../buttons-20250224T111359Z-001/buttons";
import ToastStore from "../../stores/ToastStore";

const STEP_TYPES = [
  { label: "Assessment", value: 0 },
  { label: "Review", value: 1 },
  { label: "Other", value: 2 }
];

const ASSIGN_TYPES = [
  { label: "Self", value: 0 },
  { label: "Job Profile", value: 1 },
  { label: "Employee", value: 2 }
];

const StepDialog = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  jobProfileList = [],
  employeeList = [],
  stepsList = [],
  goalCycleId
}) => {

  const [stepName, setStepName] = useState("");
  const [stepType, setStepType] = useState(undefined);
  const [allowPreviousReviewStepsEditing, setAllowPreviousReviewStepsEditing] = useState(false);
  const [assignType, setAssignType] = useState(undefined);
  const [jobProfileIds, setJobProfileIds] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [stepIdToReuse, setStepIdToReuse] = useState(null);
  const [allowQuestionReuse, setAllowQuestionReuse] = useState(false);

  // Populate edit data
  useEffect(() => {
    if (initialData) {
      setStepName(initialData.step_name || "");
      setStepType(Number(initialData.step_type ?? 0));
      setAssignType(Number(initialData.assign_type ?? 0));
      setAllowPreviousReviewStepsEditing(Number(initialData?.allow_previous_step_editing) === 1);
      if (initialData.assign_type === 1) {
        setJobProfileIds(initialData.assignee_list || []);
        setEmployeeId(null);
      } else if (initialData.assign_type === 2) {
        setEmployeeId(initialData.assignee_list?.[0] || null);
        setJobProfileIds([]);
      } else {
        setJobProfileIds([]);
        setEmployeeId(null);
      }
    } else {
      resetForm();
    }
  }, [initialData]);

  const resetForm = () => {
    setStepName("");
    setStepType(undefined);
    setAssignType(undefined);
    setAllowPreviousReviewStepsEditing(undefined);
    setJobProfileIds([]);
    setEmployeeId(null);
    setStepIdToReuse(null);
    setAllowQuestionReuse(false);
  };

  //  Derived payload list
  const getAssigneeList = () => {
    if (assignType === 1) return jobProfileIds;
    if (assignType === 2 && employeeId) return [employeeId];
    return [];
  };

  //  Job profile multi-select
  const handleJobProfileSelect = (id) => {
    setJobProfileIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  //  Remove job profile
  const removeJobProfile = (id) => {
    setJobProfileIds((prev) => prev.filter((item) => item !== id));
  };

  //  Employee select
  const handleEmployeeSelect = (id) => {
    setEmployeeId(id);
  };

  const removeEmployee = () => {
    setEmployeeId(null);
  };

  //  Save
  const handleSave = () => {
    if (!stepName.trim()) {
      ToastStore.showToast("Step name is required", "danger");
      return;
    }
    if (stepType === null || stepType === undefined) {
      ToastStore.showToast("Step type is required", "danger");
      return;
    }
    if (assignType === null || assignType === undefined) {
      ToastStore.showToast("Assign type is required", "danger");
      return;
    }
    if (stepType === 1 && assignType === 0) {
      ToastStore.showToast("Review type cannot be assigned to self", "danger");
      return;
    }
    if (assignType !== 0 && getAssigneeList().length === 0) {
      ToastStore.showToast("Please select assignee", "danger");
      return;
    }

    const payload = {
      step_name: stepName,
      step_type: stepType,
      allow_previous_step_editing: allowPreviousReviewStepsEditing,
      assign_type: assignType,
      assignee_list: getAssigneeList(),
      goal_setting_id: goalCycleId,
      allow_question_reuse: allowQuestionReuse,
      step_id_to_reuse: allowQuestionReuse ? stepIdToReuse : null
    };

    onSave(payload);
    resetForm();
  };


  const isPredefined = initialData?.predefined === true;
  const shouldDisableAssignType = isPredefined && Number(initialData?.step_type) === 0;

  return (

    <Dialog
      isOpen={isOpen}
      onClose={() => {
        if (!initialData) {
          resetForm();
        }
        onClose();
      }}
      title={initialData ? "Edit Step" : "Add Step"}
      className="modern-step-dialog"
      portalContainer={document.body}
    >
      <div className="modern-step-wrapper" >

        {/*  Step Name */}
        <FormGroup label="Step Name" labelFor="step-name">
          <InputGroup
            large
            placeholder="Manager review"
            value={stepName}
            onChange={(e) => setStepName(e.target.value)}
            style={{ height: "35px", width: "100%", borderRadius: "4px" }}
          />
        </FormGroup>

        {/*  Step Type */}
        <div>
          <div className="field-label push-10-b">Step type</div>

          <Flexbox gap="10px">
            {STEP_TYPES.map((type) => {
              const disableSelfForCustom = !isPredefined && type.value === 0;
              return (
                <button
                  key={type.value}
                  type="button"
                  disabled={isPredefined || disableSelfForCustom}
                  className={`pill-btn ${stepType === type.value
                    ? "active"
                    : ""
                    } ${isPredefined || disableSelfForCustom
                      ? "disabled"
                      : ""
                    }`}
                  // className={`pill-btn ${
                  //   stepType == type.value ? "active" : ""
                  // } ${
                  //   isPredefined || disableSelfForCustom
                  //     ? "disabled"
                  //     : ""
                  // }`}
                  onClick={() => {
                    if (isPredefined || disableSelfForCustom) return;
                    setStepType(type.value);
                  }}
                >
                  {type.label}
                </button>
              );
            })}
          </Flexbox>
        </div>

        {/* Allow Previous Step Editing */}
        {stepType === 2 && (
          <div style={{ marginTop: "16px" }}>
            <label htmlFor="allow-previous-review-steps-editing" style={{
              display: "flex", alignItems: "center",
              gap: "10px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 500
            }}
            >
              <input
                id="allow-previous-review-steps-editing"
                type="checkbox"
                checked={allowPreviousReviewStepsEditing}
                onChange={(e) =>
                  setAllowPreviousReviewStepsEditing(e.target.checked)
                }
              />

              Enable Previous Step Editing
            </label>
          </div>
        )}


        {/*  Assign Type */}
        <div>
          <div className="field-label push-10-b">Assign type</div>
          <ScrollableDropdown
            id="assign-type"
            width={400}
            disabled={shouldDisableAssignType}
            large
            style={{ height: "35px", width: "100%" }}
            label={
              assignType !== null && assignType !== undefined
                ? ASSIGN_TYPES.find((t) => t.value === assignType)?.label
                : "Select Assign Type"
            }
            // label={assignType ? ASSIGN_TYPES.find((t) => t.value === assignType)?.label : "Select Assign Type"}

            items={ASSIGN_TYPES}
            getKey={(a) => a.value}
            getLabel={(a) => a.label}
            onSelect={(a) => {
              if (shouldDisableAssignType) {
                return;
              }
              setAssignType(a.value);
            }}
          />
        </div>

        {/*  JOB PROFILE MULTI SELECT */}
        {assignType === 1 && (
          <div>
            <ScrollableDropdown
              width={400}
              large
              style={{ height: "35px", width: "100%" }}
              label="Select Job Profiles"
              items={jobProfileList}
              getKey={(p) => p.id}
              getLabel={(p) => p.name}
              enableSearch
              onSelect={(profile) => handleJobProfileSelect(profile.id)}
            />

            {/* Chips */}
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {jobProfileIds.map((id) => {
                const profile = jobProfileList.find((p) => p.id === id);
                return (
                  <div
                    key={id}
                    style={{
                      padding: "6px 12px",
                      background: "#eaf2ff",
                      borderRadius: 20,
                      display: "flex",
                      alignItems: "center",
                      gap: 6
                    }}
                  >
                    {profile?.name}
                    <span
                      style={{ cursor: "pointer", color: "#e53935" }}
                      onClick={() => removeJobProfile(id)}
                    >
                      ✕
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/*  EMPLOYEE SINGLE SELECT */}
        {assignType === 2 && (
          <div>
            <ScrollableDropdown
              width={400}
              large
              style={{ height: "35px", width: "100%" }}
              label={
                employeeId
                  ? (() => {
                    const emp = employeeList.find((e) => e.id === employeeId);
                    return emp
                      ? `${emp.first_name} ${emp.last_name || ""}`
                      : "Select Employee";
                  })()
                  : "Select Employee"
              }
              items={employeeList}
              getKey={(e) => e.id}
              getLabel={(e) => `${e.first_name} ${e.last_name || ""}`}
              enableSearch
              onSelect={(emp) => handleEmployeeSelect(emp.id)}
            />

            {/* Selected */}
            {employeeId && (
              <div style={{ marginTop: 10 }}>
                {(() => {
                  const emp = employeeList.find((e) => e.id === employeeId);
                  return (
                    <div
                      style={{
                        padding: "6px 12px",
                        background: "#fff3e0",
                        borderRadius: 20,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6
                      }}
                    >
                      {emp?.first_name} {emp?.last_name}
                      <span
                        style={{ cursor: "pointer", color: "#e53935" }}
                        onClick={removeEmployee}
                      >
                        ✕
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/*  Copy From Existing Step */}
        <div
          style={{
            marginTop: "24px",
            padding: "14px 16px",
            background: allowQuestionReuse ? "#F5F9FF" : "#FAFAFA",
            border: `1px solid ${allowQuestionReuse ? "#C9DEFF" : "#E4E7EC"
              }`,
            borderRadius: "10px"
          }}
        >

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              cursor: "pointer"
            }}
          >
            <input
              type="checkbox"
              checked={allowQuestionReuse}
              style={{ marginTop: "3px" }}
              onChange={(e) => {
                setAllowQuestionReuse(e.target.checked);

                if (!e.target.checked) {
                  setStepIdToReuse(null);
                }
              }}
            />

            <div>

              <div
                style={{
                  fontWeight: 600,
                  fontSize: "14px"
                }}
              >
                {/* Start With Questions From Another Step */}
                Copy Existing Step Questions
              </div>

              <div
                style={{
                  marginTop: "4px",
                  fontSize: "12px",
                  color: "#667085",
                  lineHeight: "18px"
                }}
              >
                Questions from the selected step will be copied into this new step after saving.
              </div>

            </div>
          </label>

          {allowQuestionReuse && (
            <div style={{ marginTop: "14px", marginLeft: "24px" }}>

              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#344054"
                }}
              >
                Source Step
              </div>

              <ScrollableDropdown
                width={400}
                large
                style={{
                  height: "35px",
                  width: "100%"
                }}
                label={
                  stepIdToReuse
                    ? stepsList.find(
                      (s) => s.id === stepIdToReuse
                    )?.step_name
                    : "Select a step"
                }
                items={stepsList.filter(
                  (s) => s.id !== initialData?.id
                )}
                getKey={(s) => s.id}
                getLabel={(s) => s.step_name}
                enableSearch
                onSelect={(step) => {
                  setStepIdToReuse(step.id);
                }}
              />

              <div
                style={{
                  marginTop: "8px",
                  fontSize: "11px",
                  color: "#98A2B3"
                }}
              >
                Questions will be copied after saving this step.
              </div>

            </div>
          )}

        </div>

        {/*  Actions */}
        <Flexbox flexDirection="row" gap="10px" justifyContent="flex-start" alignItems="center" className="mt-20">
          <FilledButton text="Save" onClick={handleSave} style={{ marginRight: "10px" }} />
          <OutlinedButton text="Cancel" onClick={() => { if (!initialData) { resetForm(); } onClose(); }} />
        </Flexbox>
      </div>
    </Dialog>
  );
};

export default StepDialog;